import React from 'react';
import {Drawer, Form, Button, Select, Tag} from 'antd';
import _ from 'lodash';

import {AccountRoleManagementDrawerProps, CustomTagRenderProps} from '../../../types/accountTypes';
import {showMessages, messageType} from '../../../helpers/message';
import {
    useGetAllAvailableRolesQuery,
    useUpdateProfileRolesMutation
} from '../../../store/api/accountApi';
import {getRoleColor} from '../../../helpers/account';
import {BaseError} from '../../../types/apiTypes';

import './AccountRoleManagementDrawer.less';

// Custom tagRender function to apply dynamic colors.
const tagRender = (props: CustomTagRenderProps) => {
    const {value, closable, onClose} = props;

    // Prevent the default mouse down behavior (to avoid triggering other events).
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const formattedValue = value.slice(value.indexOf('.') + 1);
    const displayValue = _.upperFirst(formattedValue);

    const roleColor = getRoleColor(value);

    return (
        <Tag
            color={roleColor}
            onMouseDown={onPreventMouseDown}
            closable={closable}
            onClose={onClose}
            className="role-management-drawer-tag"
        >
            {displayValue}
        </Tag>
    );
};

const AccountRoleManagementDrawer = ({
    profile,
    visible,
    onClose,
    accountID,
    oneOwnerRole
}: AccountRoleManagementDrawerProps) => {
    const [form] = Form.useForm();

    const {data: allRoles = [], error: rolesError} = useGetAllAvailableRolesQuery({
        roleType: 'user',
        roleSubType: 'owner,driver,booker,regular'
    });
    const [updateProfileRoles, {
        isLoading: isUpdatingRoles,
        error: updateError,
        isSuccess
    }] = useUpdateProfileRolesMutation();

    React.useEffect(() => {
        if (rolesError || updateError) {
            showMessages((updateError as BaseError).data?.message, messageType.error);
        } else if (isSuccess) {
            showMessages('Roles have been successfully updated.', messageType.success);
        }
    }, [rolesError, updateError, isSuccess]);

    React.useEffect(() => {
        form.setFieldsValue({
            roles: profile.roles || []
        });
    }, [profile, form]);

    const onSubmit = (values: { roles: string[] }) => {
        const updatedRoles = JSON.stringify([...values.roles].sort());
        const prevRoles = JSON.stringify([...profile.roles || []].sort());

        if (updatedRoles === prevRoles) {
            showMessages('Nothing to update!', messageType.error);
            return;
        }

        // Check if trying to remove the last owner role.
        const hasOwnerRole = profile.roles?.includes('user.owner');
        const newOwnerRoles = values.roles.filter(role => role === 'user.owner');
        const isLastOwnerRole = oneOwnerRole && hasOwnerRole && newOwnerRoles.length === 0;

        if (isLastOwnerRole) {
            showMessages('At least one profile on the account must have the owner role.', messageType.error);
            return;
        }

        const rolesToSubmit = values.roles.map(role =>
            role.startsWith('user.') ? role : `user.${role}`
        );

        updateProfileRoles({profileID: profile.id, roles: rolesToSubmit, accountID});
        handleCancel();
    };

    // Reset form values to initial state when drawer is closed.
    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    return (
        <Drawer
            title="Edit"
            width={400}
            onClose={handleCancel}
            open={visible}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    roles: profile.roles || [],
                }}
                onFinish={onSubmit}
            >
                <Form.Item
                    name="roles"
                    label="Roles"
                >
                    <Select
                        mode="multiple"
                        className="roles-select"
                        placeholder="Select or search for roles"
                        tagRender={tagRender}
                        options={allRoles.map(role => ({
                            value: role,
                            label: (
                                <Tag color={getRoleColor(role)}>
                                    {_.upperFirst(role.replace('user.', ''))}
                                </Tag>
                            )
                        }))}
                    />
                </Form.Item>

                <div className="drawer-footer">
                    <Button className="role-management-drawer-cancel" onClick={handleCancel}>Cancel</Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={isUpdatingRoles}
                    >
                        Save
                    </Button>
                </div>
            </Form>
        </Drawer>
    );
};

export default AccountRoleManagementDrawer;
